import { Shield, AlertTriangle, Mail, BarChart2 } from 'lucide-react';

export interface EmailMetric {
  label: string;
  value: string;
  icon: typeof Shield;
}

export interface EmailDomainStatus {
  domain: string;
  spf: 'pass' | 'fail';
  dkim: 'configured' | 'missing';
  dmarc: 'enforced' | 'quarantine' | 'none';
  mta: 'secure' | 'warning';
}

export interface ThreatStat {
  type: string;
  count: number;
  trend: string;
  status: 'warning' | 'success';
}

export interface SecurityAlert {
  title: string;
  severity: 'high' | 'medium' | 'low';
  time: string;
}

export interface DomainAlert {
  title: string;
  type: 'warning' | 'critical' | 'info';
  description: string;
}

export interface DomainRecommendation {
  title: string;
  description: string;
}

export interface EmailAlert {
  title: string;
  type: 'critical' | 'warning' | 'info';
  description: string;
}

export interface EmailRecommendation {
  title: string;
  description: string;
}

export const emailMetrics: EmailMetric[] = [
  { label: 'Protected Domains', value: '12', icon: Shield },
  { label: 'Potential Threats Blocked', value: '2.4K', icon: AlertTriangle },
  { label: 'Potential Phishing Attempts', value: '156', icon: Mail },
  { label: 'DMARC Reports', value: '1.2K', icon: BarChart2 }
];

export const emailDomainStatuses: EmailDomainStatus[] = [
  { domain: 'example.com', spf: 'pass', dkim: 'configured', dmarc: 'enforced', mta: 'secure' },
  { domain: 'mail.example.com', spf: 'pass', dkim: 'missing', dmarc: 'none', mta: 'warning' },
  { domain: 'marketing.example.com', spf: 'fail', dkim: 'configured', dmarc: 'quarantine', mta: 'secure' }
];

export const threatStats: ThreatStat[] = [
  { type: 'Potential Phishing Attempts', count: 156, trend: '+12%', status: 'warning' },
  { type: 'Potential Spams Blocked', count: 2345, trend: '-5%', status: 'success' },
  { type: 'Potential Malwares Campaigns Detected', count: 23, trend: '+3%', status: 'warning' }
];

export const securityAlerts: SecurityAlert[] = [
  { title: 'DMARC Policy Violation', severity: 'high', time: '2 hours ago' },
  { title: 'SPF Record Missing', severity: 'medium', time: '5 hours ago' },
  { title: 'TLS Certificate Expiring', severity: 'medium', time: '1 day ago' },
  { title: 'DKIM Signature Invalid', severity: 'high', time: '2 days ago' },
  { title: 'New Email Gateway Detected', severity: 'low', time: '3 days ago' },
];

export const domainAlerts: DomainAlert[] = [
  { 
    title: 'TLS Certificate Expiring', 
    type: 'warning',
    description: 'Certificate for example.com will expire in 15 days' 
  },
  { 
    title: 'DNS Misconfiguration', 
    type: 'critical',
    description: 'NS records for domain.com are inconsistent' 
  },
  { 
    title: 'Domain Reputation Issue', 
    type: 'warning',
    description: 'Your domain has been flagged in spam databases' 
  },
  { 
    title: 'Domain Monitoring Enabled', 
    type: 'info',
    description: 'Continuous monitoring has been enabled for all domains' 
  },
];

export const domainRecommendations: DomainRecommendation[] = [
  {
    title: 'Renew TLS Certificate',
    description: 'Update your TLS certificate before it expires to maintain secure connections'
  },
  {
    title: 'Configure DNSSEC',
    description: 'Enable DNSSEC to protect your domain from DNS poisoning attacks'
  },
  {
    title: 'Update NS Records',
    description: 'Ensure your nameserver records are consistent across all registrars'
  },
  {
    title: 'Enable CAA Records',
    description: 'Add Certificate Authority Authorization records to restrict which CAs can issue certificates'
  },
];

export const emailAlerts: EmailAlert[] = [
  { 
    title: 'DMARC Policy Violation', 
    type: 'critical',
    description: '15 emails failed DMARC checks in the last 24 hours' 
  },
  { 
    title: 'SPF Record Incomplete', 
    type: 'warning',
    description: 'SPF record does not include all sending servers' 
  },
  { 
    title: 'DKIM Key Rotation Due', 
    type: 'info',
    description: 'DKIM keys should be rotated every 6 months for security' 
  },
  { 
    title: 'Email Spoofing Attempt', 
    type: 'critical',
    description: 'Multiple attempts to spoof your domain detected' 
  },
];

export const emailRecommendations: EmailRecommendation[] = [
  {
    title: 'Implement DMARC Reporting',
    description: 'Set up DMARC reporting to monitor email authentication failures'
  },
  {
    title: 'Strengthen SPF Policy',
    description: 'Update your SPF record with a hard fail (~all → -all) to prevent email spoofing'
  },
  {
    title: 'Enable MTA-STS',
    description: 'Configure MTA-STS to enforce TLS encryption for all incoming mail'
  },
  {
    title: 'Rotate DKIM Keys',
    description: 'Update your DKIM keys regularly to maintain email security'
  },
];